import React from 'react';
import { ResponsiveLine } from '@nivo/line';
import format from 'date-fns/format';
import { enUS, ru } from 'date-fns/locale';

import { getTranslation } from 'helpers/getTranslation';

import { Typography } from 'components/shared/Typography';

import {
  hasPromocodeActivationData,
  preparePromocodeActivationsChartData,
  type PromocodeActivationStatsItem,
} from './PromocodeActivationsChart.helpers';

import styles from './PromocodeActivationsChart.module.scss';

type AnyResponsiveLineProps = Record<string, unknown>;

const CustomPoint = (props: AnyResponsiveLineProps) => {
  const { currentSlice } = props as {
    currentSlice?: { points?: Array<{ x: number; y: number }> };
  };

  const currentPoint = currentSlice?.points?.[0];

  if (currentPoint) {
    return (
      <g style={{ pointerEvents: 'none' }}>
        <circle
          fill="#85E0AB"
          r={5}
          strokeWidth={2}
          stroke="#fff"
          cx={currentPoint.x}
          cy={currentPoint.y}
        />
      </g>
    );
  }

  return null;
};

const ChartTooltip = ({
  locale,
  slice,
}: {
  locale: string;
  slice?: AnyResponsiveLineProps;
}) => {
  const currentSlice = (slice as { points?: Array<{ data?: { x?: string; y?: number } }> })
    ?.points?.[0];

  const currentValue = currentSlice?.data?.y ?? 0;
  const rawX = currentSlice?.data?.x;
  const currentDate =
    rawX !== undefined && rawX !== null
      ? format(new Date(String(rawX)), 'MMM yyyy', {
          locale: locale === 'ru' ? ru : enUS,
        })
      : '';

  return (
    <div className={styles.tooltip}>
      <Typography
        className={styles.tooltipText}
        variant="caption"
        mode="compact"
      >
        {getTranslation('school_page.activation_codes.chart.tooltip', {
          currentDate,
          currentValue,
        })}
      </Typography>
    </div>
  );
};

const PromocodeActivationsChart = ({
  locale = 'en',
  stats,
}: {
  locale?: string;
  stats: PromocodeActivationStatsItem[];
}) => {
  const hasData = hasPromocodeActivationData(stats);
  const lineData = preparePromocodeActivationsChartData(stats);

  const axisBottom = {
    orient: 'bottom',
    tickPadding: 5,
    tickSize: 0,
    format: (value: Date | number | string) =>
      format(
        typeof value === 'string' || typeof value === 'number'
          ? new Date(value)
          : value,
        'MMM yyyy',
        {
          locale: locale === 'ru' ? ru : enUS,
        },
      ),
    tickValues: 'every month',
  };

  const lineProps: AnyResponsiveLineProps = {
    colors: { datum: 'color' },
    data: lineData,
    margin: { top: 16, right: 24, bottom: 40, left: 52 },
    xScale: {
      type: 'time',
      format: '%Y-%m-%d',
      useUTC: false,
      precision: 'month',
    },
    xFormat: 'time:%Y-%m-%d',
    yScale: {
      type: 'linear',
      stacked: false,
    },
    axisLeft: {
      orient: 'left',
      tickPadding: 5,
      tickSize: 0,
    },
    axisBottom,
    theme: {
      crosshair: {
        line: {
          stroke: '#DFE9F9',
          strokeWidth: 2,
          strokeDasharray: '0',
        },
      },
      axis: {
        ticks: {
          text: {
            fontSize: 10,
            lineHeight: 12,
            fontFamily: 'Inter',
            fontWeight: 500,
            fill: '#5C738C',
          },
        },
      },
    },
    enableGridX: false,
    enableGridY: false,
    enablePoints: false,
    lineWidth: 4,
    enableSlices: 'x',
    crosshairType: 'top-left',
    useMesh: true,
    layers: [
      'grid',
      'markers',
      'axes',
      'areas',
      'crosshair',
      'lines',
      'points',
      'slices',
      'mesh',
      'legends',
      CustomPoint,
    ],
    sliceTooltip: (props: AnyResponsiveLineProps) =>
      ChartTooltip({ ...props, locale }),
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <Typography
          className={styles.title}
          variant="body1"
          weight="medium"
          mode="compact"
        >
          {getTranslation('school_page.activation_codes.chart.title')}
        </Typography>
        <Typography className={styles.subtitle} variant="caption" mode="compact">
          {getTranslation('school_page.activation_codes.chart.subtitle')}
        </Typography>
      </div>

      {hasData ? (
        <div className={styles.chart}>
          <ResponsiveLine {...(lineProps as any)} />
        </div>
      ) : (
        <div className={styles.emptyState}>
          <Typography
            className={styles.emptyStateText}
            variant="body3"
            mode="compact"
          >
            {getTranslation('school_page.activation_codes.chart.empty')}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default React.memo(PromocodeActivationsChart);
